import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getBlobApiErrorMessage } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
// One exhaustive map, shared with InspectionDetailPage and the field screen.
// This column used to be a chain of six inline ternaries saying the same thing
// a third time; a seventh form type would have had to be added in three places.
import { inspectionFormTypeLabel } from '../lib/inspection-labels';
import { invalidateSafety } from '../lib/safety-invalidation';
import { toast } from 'sonner';
import {
  Search, Download, FileText,
  ClipboardCheck, AlertTriangle, Wrench, Shield,
  ChevronRight, Loader2, Eye, ClipboardList, Calendar,
  CheckCircle, XCircle, Pencil, FilePen,
} from 'lucide-react';
import JSADetailModal from '../components/JSADetailModal';
import IncidentDetailModal from '../components/IncidentDetailModal';
import { formatCalendarDate, formatInstant, formatInstantDay, todayCalendarDate } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import { isJsaEditable, isJsaSubmittable, jsaEditPath, rejectionReasonText } from '../lib/jsa';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
  type LoadFailureKind,
} from '../lib/load-failure';
import { type TripInspectionListRow, type TripInspectionType, TRIP_TYPES, TRIP_TYPE_LABEL, personName, reportOutcome, tripTypeLabel } from '../lib/trip-inspections';
import { OutcomeBadge } from '../pages/trucking/TripInspectionsScreen';
import { normalizeInspection } from '../pages/rentals/RentalInspections';
import { unwrapArray } from '../lib/api-envelope';
import { downloadSubmissionPdf, submissionPdfFilename } from '../lib/submission-pdf';
import { saveBlobAs } from '../lib/blob-tab';

type RecordType = 'meetings' | 'jsas' | 'incidents' | 'inspections' | 'near_misses' | 'custom_forms';

const TABS: { key: RecordType; label: string; icon: any }[] = [
  { key: 'meetings', label: 'Meetings', icon: ClipboardCheck },
  { key: 'jsas', label: 'JSAs', icon: FileText },
  { key: 'incidents', label: 'Incidents', icon: AlertTriangle },
  { key: 'inspections', label: 'Inspections', icon: Wrench },
  { key: 'near_misses', label: 'Near Misses', icon: Shield },
  { key: 'custom_forms', label: 'Custom Forms', icon: ClipboardList },
];

/* SATURATED SIGNAL FILLS, DARK INK — the register's badge idiom. The pale 10-20%
 * washes with a bright hue as ink read under 2:1 in light mode. Five hues, no
 * new ones: statuses share the nearest hue and the label tells them apart.
 *   green — finished and good: completed, approved, closed, pass, reviewed
 *   amber — waiting on a person: submitted, open, under investigation, conditional
 *   blue  — logged or booked, not yet acted on: reported, scheduled
 *   red   — refused or failed: flagged, rejected, fail
 *   slate — not in play: draft, cancelled
 * Every column here is a status enum from a different table, so the map is the
 * union; a value it does not know is a slate `—`, never a guessed label. */
const STATUS_BADGE: Record<string, { label: string; fill: string }> = {
  completed: { label: 'Completed', fill: 'bg-fi-signal-green' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-green' },
  closed: { label: 'Closed', fill: 'bg-fi-signal-green' },
  pass: { label: 'Pass', fill: 'bg-fi-signal-green' },
  reviewed: { label: 'Reviewed', fill: 'bg-fi-signal-green' },
  submitted: { label: 'Submitted', fill: 'bg-fi-signal-amber' },
  open: { label: 'Open', fill: 'bg-fi-signal-amber' },
  under_investigation: { label: 'Under Investigation', fill: 'bg-fi-signal-amber' },
  conditional: { label: 'Conditional', fill: 'bg-fi-signal-amber' },
  reported: { label: 'Reported', fill: 'bg-fi-signal-blue' },
  scheduled: { label: 'Scheduled', fill: 'bg-fi-signal-blue' },
  flagged: { label: 'Flagged', fill: 'bg-fi-signal-red' },
  rejected: { label: 'Rejected', fill: 'bg-fi-signal-red' },
  fail: { label: 'Fail', fill: 'bg-fi-signal-red' },
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate' },
  cancelled: { label: 'Cancelled', fill: 'bg-fi-signal-slate' },
};
const UNKNOWN_STATUS = { label: '\u2014', fill: 'bg-fi-signal-slate' };

const CF_STATUS_FILTERS = ['All', 'Submitted', 'Reviewed', 'Flagged', 'Draft'] as const;

/** An `_at` INSTANT, shown as the TENANT's day (`companies.time_zone`). */
function fmtDate(d: string | null, timeZone: string | null) {
  return formatInstantDay(d, timeZone, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';
}

/** meeting/incident/inspection dates are calendar days; `_at` columns are not. */
function fmtDay(d: string | null | undefined) {
  return formatCalendarDate(d, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';
}

function StatusBadge({ status }: { status: string }) {
  const entry = STATUS_BADGE[status] ?? UNKNOWN_STATUS;
  return (
    <span className={`inline-flex rounded-fi-pill border border-black/20 px-2 py-0.5 text-[10px] font-bold uppercase text-fi-signal-ink ${entry.fill}`}>
      {entry.label}
    </span>
  );
}

/** A failed or refused list read, in the place its empty state would have been. */
function FailureRow({ colSpan, query, kind, subject }: {
  colSpan: number; query: unknown; kind: LoadFailureKind; subject: string;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-6">
        <LoadFailureNotice error={queryFailureError(query as any)} kind={kind} subject={subject} />
      </td>
    </tr>
  );
}

function LoadingRow({ colSpan }: { colSpan: number }) {
  return (
    <tr><td colSpan={colSpan} className="px-4 py-8 text-center text-fieldiq-muted">
      <Loader2 size={18} className="mx-auto animate-spin" aria-label="Loading" />
    </td></tr>
  );
}

/**
 * A row from GET /safety/jsa — typed to what that route actually returns.
 *
 * The list route selects `getTableColumns(jobSafetyAnalysis)` plus `jobNumber`
 * from a LEFT JOIN on jobs, so the payload is camelCase throughout. The
 * snake_case twins this screen used to read through (`job_number`,
 * `created_at`, `rejection_reason`) are not in it — they belong to
 * GET /safety/jsa/:id, which is a raw `SELECT jsa.*, j.job_number`. The two
 * routes do not agree, and reading both spellings off the list row hid that.
 *
 * `jobNumber` is null for a job-less JSA (migration 0185 made `job_id`
 * nullable). That renders '—' correctly: there is no job to name.
 */
interface JsaRow {
  id: string;
  title: string | null;
  status: string | null;
  jobId: string | null;
  jobNumber?: string | null;
  createdAt: string | null;
  rejectionReason: string | null;
}

/**
 * A row from GET /safety/meetings — typed to what that route actually returns.
 *
 * The list route is a bare `db.select().from(safetyMeetings)`, so the payload is
 * exactly the drizzle column set, camelCase throughout. The snake_case twins
 * this screen used to read through (`meeting_type`, `job_number`) are not in it
 * and never were — reading both spellings hid that one of them was a guess.
 *
 * `jobNumber` is NOT on the list route today. `safety_meetings.job_id` is, but
 * the route selects no join, so nothing here can name the job. It is declared
 * optional and read below because that is the same gap GET /safety/jsa closed
 * by adding `jobNumber` to its column list — until the meetings route does the
 * same, the JOB column is honestly '—' for every row, including rows that DO
 * have a job. Tracked against the API, not patched around here.
 */
interface MeetingRow {
  id: string;
  jobId: string | null;
  jobNumber?: string | null;
  meetingType: string | null;
  meetingDate: string | null;
  location: string | null;
  notes: string | null;
  status: string | null;
}

const VALID_TABS: RecordType[] = ['meetings', 'jsas', 'incidents', 'inspections', 'near_misses', 'custom_forms'];

export default function SafetyRecordsScreen() {
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, hasVertical } = useAuth();
  const canApprove = ['admin', 'supervisor'].includes(user?.role);
  /* WHAT THE INSPECTIONS TAB LISTS IS A QUESTION ABOUT THE TENANT.
   * Casing: equipment inspections from /casing/*, exactly as before. Without
   * casing: trip inspections for trucking (api#855) and rental inspections for
   * surface rentals (api#861), both when the tenant has both. A tenant with
   * none of the three gets no tab: /casing/* refuses them, and a tab that could
   * only ever show a refusal is a broken tab. */
  const casingTenant = hasVertical('casing_running');
  // Trip inspections open to surface rentals too (#815, api#1043).
  const tripTab = !casingTenant && (hasVertical('trucking') || hasVertical('surface_rentals'));
  // Rental inspections (inspections PR 4), for a rentals tenant with no casing.
  // A trucking + rentals tenant sees both lists, one under the other.
  const rentalTab = !casingTenant && hasVertical('surface_rentals');
  const showInspections = casingTenant || tripTab || rentalTab;
  const validTabs = showInspections ? VALID_TABS : VALID_TABS.filter(t => t !== 'inspections');
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = validTabs.includes(searchParams.get('tab') as RecordType)
    ? (searchParams.get('tab') as RecordType)
    : 'meetings';
  const [activeTab, setActiveTabState] = useState<RecordType>(initialTab);
  /* The chip writes `?tab=` as well as the state, replacing rather than
   * pushing: the section strip above marks Custom Forms as the current tab
   * from the URL, and a chip that changed only local state left the strip
   * pointing at Records while the Custom Forms list was on screen. */
  const setActiveTab = (tab: RecordType) => {
    setActiveTabState(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  // Sync activeTab when ?tab= search param changes (browser back/forward, re-navigation)
  useEffect(() => {
    const param = searchParams.get('tab') as RecordType;
    const allowed = showInspections ? VALID_TABS : VALID_TABS.filter(t => t !== 'inspections');
    if (param && allowed.includes(param)) {
      setActiveTabState(param);
    }
  }, [searchParams, showInspections]);

  const [search, setSearch] = useState('');
  const [tripType, setTripType] = useState<'all' | TripInspectionType>('all');
  const [pdfLoading, setPdfLoading] = useState<string | null>(null);
  const [approveModalJsa, setApproveModalJsa] = useState<JsaRow | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectModalId, setRejectModalId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [submitModalJsa, setSubmitModalJsa] = useState<JsaRow | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [detailJsaId, setDetailJsaId] = useState<string | null>(null);
  const [detailIncidentId, setDetailIncidentId] = useState<string | null>(null);

  const handleApprove = async (id: string) => {
    setApprovingId(id);
    try {
      await api.safety.approveJsa(id);
      toast.success('JSA approved');
      invalidateSafety(queryClient);
      setApproveModalJsa(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Failed to approve JSA');
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectModalId || !rejectReason.trim()) return;
    setRejectSubmitting(true);
    try {
      await api.safety.rejectJsa(rejectModalId, { rejection_reason: rejectReason.trim() });
      toast.success('JSA rejected');
      invalidateSafety(queryClient);
      setRejectModalId(null);
      setRejectReason('');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Failed to reject JSA');
    } finally {
      setRejectSubmitting(false);
    }
  };

  const handleSubmitJsa = async (id: string) => {
    setSubmittingId(id);
    try {
      await api.safety.submitJsa(id);
      toast.success('JSA submitted for approval');
      invalidateSafety(queryClient);
      setSubmitModalJsa(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Failed to submit JSA');
    } finally {
      setSubmittingId(null);
    }
  };

  // ── Custom Forms tab filter state ──
  const [cfStatusFilter, setCfStatusFilter] = useState<string>('All');
  const [cfTemplateFilter, setCfTemplateFilter] = useState<string>('');
  const [cfDateFrom, setCfDateFrom] = useState('');
  const [cfDateTo, setCfDateTo] = useState('');
  const [cfPage, setCfPage] = useState(1);
  /** What this screen ASKS for. What it got back is `per_page` on the response. */
  const cfRequestedPerPage = 20;

  /* ── Data queries for tabs 1-5 ──
   * EVERY LIST HAS THREE STATES NOW. These were `{ data }` then `?? []`, so a
   * refusal or a dead zone read "No meetings found" / "No JSAs found" — and
   * so did a request still in flight, since four of the five had no loading
   * branch at all. Keys and fetchers are unchanged; the options add the
   * refusal-aware retry and `networkMode: 'always'` so a failure surfaces as
   * one rather than sitting paused (see `queryLoadFailure`). */
  const meetingsQuery = useQuery({
    queryKey: ['safety-records-meetings'],
    queryFn: () => api.safety.meetings().then(r => r.data.data ?? r.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const jsaQuery = useQuery({
    queryKey: ['safety-records-jsas'],
    queryFn: () => api.safety.jsaList().then(r => r.data.data ?? r.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const incidentsQuery = useQuery({
    queryKey: ['safety-records-incidents'],
    queryFn: () => api.safety.incidents().then(r => r.data.data ?? r.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const nearMissQuery = useQuery({
    queryKey: ['safety-records-near-misses'],
    queryFn: () => api.safety.nearMisses().then(r => r.data.data ?? r.data ?? []),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const inspectionsQuery = useQuery({
    queryKey: ['safety-records-inspections'],
    queryFn: () => api.casingInspections.list({ status: 'submitted' }).then(r => r.data.data ?? r.data ?? []),
    enabled: casingTenant,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const tripQuery = useQuery<TripInspectionListRow[]>({
    queryKey: ['trip-inspections', 'list', 'all'],
    queryFn: () => api.trucking.tripInspections.list().then(r => r.data?.data ?? []),
    enabled: tripTab,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const tripFailure = tripTab ? (tripQuery.data === undefined ? queryLoadFailure(tripQuery) : null) : null;
  const rentalQuery = useQuery({
    queryKey: ['rentals', 'inspections', 'records', timeZone],
    queryFn: () => api.rentals.inspections.list().then(r => unwrapArray<RentalInspectionApiRow>(r.data).map(row => normalizeInspection(row, timeZone))),
    enabled: rentalTab,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const rentalFailure = rentalTab ? (rentalQuery.data === undefined ? queryLoadFailure(rentalQuery) : null) : null;
  const meetingsData = meetingsQuery.data;
  const jsaData = jsaQuery.data;
  const incidentsData = incidentsQuery.data;
  const nearMissData = nearMissQuery.data;
  const inspectionsData = inspectionsQuery.data;

  /** Failed only if nothing arrived: rows from an earlier read are still real. */
  const failureOf = (q: { data?: unknown } & Parameters<typeof queryLoadFailure>[0]) =>
    q.data === undefined ? queryLoadFailure(q) : null;
  const meetingsFailure = failureOf(meetingsQuery);
  const jsaFailure = failureOf(jsaQuery);
  const incidentsFailure = failureOf(incidentsQuery);
  const nearMissFailure = failureOf(nearMissQuery);
  const inspectionsFailure = failureOf(inspectionsQuery);
  /** In flight: no answer yet, and no failure either. */
  const pendingOf = (q: { data?: unknown }, failure: LoadFailureKind | null) =>
    q.data === undefined && !failure;
  const inspectionsLoading = pendingOf(inspectionsQuery, inspectionsFailure);

  // ── Custom Forms tab: template list + filtered submissions ──
  /**
   * The filter dropdown: active AND draft.
   *
   * `status: 'active'` hid every unpublished template, and a draft is not a
   * template with no submissions — `createTemplate` and `cloneTemplate` both
   * write `status = 'draft'` and nothing moves it without an explicit Publish,
   * so tenant forms sit in draft for months with real submissions filed
   * against them. Filtering by one was impossible: the template was simply
   * absent from the list, with no indication it existed.
   *
   * The comma form is expanded server-side into `status = ANY(...)` (API #319).
   * Naming both is NOT the same as sending no filter, which would also return
   * archived templates.
   */
  const { data: cfTemplates = [] } = useQuery({
    queryKey: ['safety-templates-list', 'active,draft'],
    queryFn: () => api.safety.templates({ status: 'active,draft' }).then(r => r.data?.data ?? r.data ?? []),
    enabled: activeTab === 'custom_forms',
  });

  /** The server-side filters, shared by the list and its CSV export so the two cannot disagree. */
  const cfFilterParams = () => {
    const params: Record<string, string> = {};
    if (cfStatusFilter !== 'All') params.status = cfStatusFilter.toLowerCase();
    /* THE DRAFT CHIP IS THE READER'S OWN DRAFTS. A draft is unfinished work
     * that only its author can save or submit (the update route matches
     * `submitted_by`), so somebody else's draft is a row this reader can do
     * nothing with. `submitted_by` rides beside `mine` for the reason
     * MyDraftsList gives: an API that predates `mine` ignores it. */
    if (cfStatusFilter === 'Draft' && user?.id) {
      params.mine = 'true';
      params.submitted_by = user.id;
    }
    if (cfTemplateFilter) params.template_id = cfTemplateFilter;
    if (cfDateFrom) params.date_from = cfDateFrom;
    if (cfDateTo) params.date_to = cfDateTo;
    return params;
  };
  const [cfExporting, setCfExporting] = useState(false);
  const exportCustomFormsCsv = async () => {
    setCfExporting(true);
    try {
      const res = await api.safety.exportSubmissionsCsv(cfFilterParams());
      saveBlobAs(res.data as Blob, `custom-forms-${todayCalendarDate(timeZone) ?? 'export'}.csv`);
    } catch (err) {
      toast.error(await getBlobApiErrorMessage(err, 'Could not export the CSV'));
    } finally {
      setCfExporting(false);
    }
  };

  const cfSubmissionsQuery = useQuery({
    queryKey: ['safety-records-custom-forms', cfStatusFilter, cfTemplateFilter, cfDateFrom, cfDateTo, cfPage, user?.id],
    queryFn: () => {
      const params: any = { page: cfPage, per_page: cfRequestedPerPage, ...cfFilterParams() };
      return api.safety.submissions(params).then(r => r.data?.data ?? r.data ?? { submissions: [], total: 0 });
    },
    enabled: activeTab === 'custom_forms',
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const cfSubmissionsData = cfSubmissionsQuery.data;
  const cfFailure = failureOf(cfSubmissionsQuery);
  const cfLoading = cfSubmissionsQuery.isLoading && !cfFailure;

  const cfSubmissions: any[] = Array.isArray(cfSubmissionsData)
    ? cfSubmissionsData
    : (cfSubmissionsData?.submissions || cfSubmissionsData || []);
  /**
   * The pager reads the RESPONSE, not what this screen asked for.
   *
   * `per_page` is the server's own answer and it is not always the number sent:
   * it clamps at 200 and falls back to 20 on a junk or zero value. Dividing the
   * total by `cfRequestedPerPage` therefore counts pages of a size the server
   * may not have used — offering pages that hold nothing, or hiding rows that
   * exist. `total_pages` is the count computed against the size actually used,
   * so it wins outright; the division survives only as the fallback for a bare
   * array or a response shape that predates the key.
   *
   * `total` arrives as a string — it is a Postgres COUNT, and node-pg returns
   * bigints as strings — so it is coerced before any arithmetic or comparison.
   */
  const rawCfTotal = cfSubmissionsData?.total;
  // The guard goes BEFORE the coercion, not after: Number(null) and Number('')
  // are both 0, so a `Number.isFinite` check on the result passes an absent
  // total through as a confident zero — and this number is the tab badge.
  const parsedCfTotal =
    rawCfTotal === null || rawCfTotal === undefined || rawCfTotal === '' ? NaN : Number(rawCfTotal);
  const cfTotal = Number.isFinite(parsedCfTotal)
    ? parsedCfTotal
    : Array.isArray(cfSubmissions)
      ? cfSubmissions.length
      : 0;
  const cfServerPerPage = Number(cfSubmissionsData?.per_page) || cfRequestedPerPage;
  const cfTotalPages =
    Number(cfSubmissionsData?.total_pages) || Math.max(1, Math.ceil(cfTotal / cfServerPerPage));

  const meetings: MeetingRow[] = Array.isArray(meetingsData) ? meetingsData : [];
  const jsas: JsaRow[] = Array.isArray(jsaData) ? jsaData : [];
  const incidents: any[] = Array.isArray(incidentsData) ? incidentsData : [];
  const nearMisses: any[] = Array.isArray(nearMissData) ? nearMissData : [];
  const inspections: any[] = Array.isArray(inspectionsData) ? inspectionsData : [];

  // Filter by search (tabs 1-5 only — Custom Forms uses server-side filters)
  const filterBySearch = (items: any[], fields: string[]) => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(item =>
      fields.some(f => {
        const val = item[f];
        return val && String(val).toLowerCase().includes(q);
      })
    );
  };

  const filteredMeetings: MeetingRow[] = filterBySearch(meetings, ['location', 'notes', 'jobNumber', 'meetingType']);
  const filteredJsas: JsaRow[] = filterBySearch(jsas, ['title', 'jobNumber', 'status']);
  const filteredIncidents = filterBySearch(incidents, ['description', 'location', 'incidentNumber', 'incident_number', 'incidentType', 'incident_type']);
  const filteredNearMisses = filterBySearch(nearMisses, ['title', 'description', 'location']);
  const filteredInspections = filterBySearch(inspections, ['form_type', 'operator_name', 'inspector_name', 'tong_make', 'serial_number']);
  const filteredTrips: TripInspectionListRow[] = filterBySearch(tripQuery.data ?? [], ['location', 'inspector_name', 'driver_first_name', 'driver_last_name'])
    .filter((r: TripInspectionListRow) => tripType === 'all' || r.inspection_type === tripType);
  const filteredRentals = filterBySearch(rentalQuery.data ?? [], ['asset_name', 'asset_tag', 'performed_by']);

  // Also apply search locally on custom forms results for page-level consistency
  const filteredCustomForms = filterBySearch(cfSubmissions, ['template_name', 'template_category', 'submitter_name']);

  /* A draft of the reader's own reopens in the fill screen, prefilled, under
   * the same row id; every other row opens its read view. Somebody else's
   * draft (it can appear under All) is read-only to this reader, so it gets
   * View, not Resume. */
  const isOwnDraft = (cf: any) => cf.status === 'draft' && !!user?.id && cf.submitted_by === user.id;
  const openCustomForm = (cf: any) =>
    navigate(isOwnDraft(cf) ? `/forms/dynamic/${cf.template_id}?draft=${cf.id}` : `/safety/submissions/${cf.id}`);

  const handleDownloadPdf = async (type: string, id: string) => {
    setPdfLoading(id);
    try {
      let res: any;
      if (type === 'meeting') res = await api.safety.getMeetingPdf(id);
      else if (type === 'jsa') res = await api.safety.getJsaPdf(id);
      else if (type === 'incident') res = await api.safety.getIncidentPdf(id);
      if (res?.data) {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}-${id.slice(0, 8)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setPdfLoading(null);
    }
  };

  /* CHIP COUNTS. Tabs 1-5 are TALLIES OF LOADED ROWS — these five lists are
   * unpaginated and no endpoint returns their counts — narrowed by the search
   * box, so the chip says how many rows its list will show. Custom Forms is
   * the server's `total`, since that list is paginated.
   *
   * A count is shown only once its list has ARRIVED: undefined while in
   * flight, after a failure, and for Custom Forms until that tab has been
   * opened (its query is lazy). A real zero is shown as a zero. The old chips
   * hid every count at zero, so an empty list and an unloaded one looked the
   * same. */
  const arrived = (q: { data?: unknown }, n: number) => (q.data === undefined ? undefined : n);
  const counts: Record<RecordType, number | undefined> = {
    meetings: arrived(meetingsQuery, filteredMeetings.length),
    jsas: arrived(jsaQuery, filteredJsas.length),
    incidents: arrived(incidentsQuery, filteredIncidents.length),
    // Non-casing: the sum of whichever lists apply, shown once every one of
    // them has arrived (a partial sum would read as a smaller real count).
    inspections: casingTenant
      ? arrived(inspectionsQuery, filteredInspections.length)
      : (tripTab && tripQuery.data === undefined) || (rentalTab && rentalQuery.data === undefined)
        ? undefined
        : (tripTab ? filteredTrips.length : 0) + (rentalTab ? filteredRentals.length : 0),
    near_misses: arrived(nearMissQuery, filteredNearMisses.length),
    custom_forms: arrived(cfSubmissionsQuery, cfTotal),
  };

  return (
    /* Under `SafetyLayout`, which owns the band, the <h1> and the section
     * strip; this page is the Records tab. */
    <>
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-fieldiq-muted" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by location, job, description..."
            className="w-full bg-fieldiq-card border border-fieldiq-border rounded-xl pl-10 pr-4 py-2.5 text-sm text-foreground placeholder-fieldiq-muted focus:border-fieldiq-accent outline-none"
          />
        </div>

        {/* Tabs */}
        <div className="scrollbar-hide flex gap-2 overflow-x-auto pb-1">
          {TABS.filter(tab => tab.key !== 'inspections' || showInspections).map(tab => {
            const Icon = tab.icon;
            const count = counts[tab.key];
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                aria-pressed={isActive}
                /* The Quotes chip: a pill, amber-filled when active. The icon
                 * and label carry an explicit ink rather than inheriting a
                 * hover colour — a hovered chip once lost both and read as a
                 * bare number. */
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                  isActive
                    ? 'bg-fi-amber text-fi-amber-ink'
                    : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                }`}
              >
                <Icon size={14} aria-hidden="true" />
                {tab.label}
                {count !== undefined && (
                  <span className={`tabular-nums ${isActive ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                    {displayValue(count)}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Custom Forms: filters (only visible on this tab) ── */}
        {activeTab === 'custom_forms' && (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs text-fieldiq-text mb-1 block">Template</label>
                <select
                  value={cfTemplateFilter}
                  onChange={e => { setCfTemplateFilter(e.target.value); setCfPage(1); }}
                  className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-2 text-foreground text-sm focus:outline-none focus:border-fieldiq-accent min-w-[180px]"
                >
                  <option value="">All Templates</option>
                  {(cfTemplates as any[]).map((t: any) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-fieldiq-text mb-1 block">From</label>
                <input
                  type="date"
                  value={cfDateFrom}
                  onChange={e => { setCfDateFrom(e.target.value); setCfPage(1); }}
                  className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-2 text-foreground text-sm focus:outline-none focus:border-fieldiq-accent"
                />
              </div>
              <div>
                <label className="text-xs text-fieldiq-text mb-1 block">To</label>
                <input
                  type="date"
                  value={cfDateTo}
                  onChange={e => { setCfDateTo(e.target.value); setCfPage(1); }}
                  className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-2 text-foreground text-sm focus:outline-none focus:border-fieldiq-accent"
                />
              </div>
              {(cfDateFrom || cfDateTo || cfTemplateFilter) && (
                <button
                  onClick={() => { setCfDateFrom(''); setCfDateTo(''); setCfTemplateFilter(''); setCfPage(1); }}
                  className="text-xs text-fieldiq-accent hover:text-fieldiq-accent/80 py-2"
                >
                  Clear filters
                </button>
              )}
              {/* Uses the template, date and status filters above. The search
                  box narrows only the loaded page, and the export endpoint
                  takes no text query, so the search is not applied to it. */}
              <button
                type="button"
                onClick={exportCustomFormsCsv}
                disabled={cfExporting}
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 bg-fieldiq-card border border-fieldiq-border rounded-lg text-sm font-medium text-foreground hover:bg-fieldiq-border disabled:opacity-50"
              >
                {cfExporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Export CSV
              </button>
            </div>

            <div className="flex gap-2">
              {CF_STATUS_FILTERS.map(s => (
                <button
                  key={s}
                  onClick={() => { setCfStatusFilter(s); setCfPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    cfStatusFilter === s
                      ? 'bg-fieldiq-accent/20 text-fieldiq-accent border border-fieldiq-accent'
                      : 'bg-fieldiq-card text-fieldiq-text hover:text-foreground border border-fieldiq-border'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Records Table */}
        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden">
          {activeTab === 'meetings' && (
            <div className="w-full overflow-x-auto">
            <table className="min-w-[640px] w-full text-sm">
              <thead>
                <tr className="border-b border-fieldiq-border text-fieldiq-muted text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-3 font-medium">Date</th>
                  <th className="text-left px-4 py-3 font-medium">Type</th>
                  <th className="text-left px-4 py-3 font-medium">Job</th>
                  <th className="text-left px-4 py-3 font-medium">Location</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-right px-4 py-3 font-medium sticky right-0 bg-fieldiq-card">Actions</th>
                </tr>
              </thead>
              <tbody>
                {meetingsFailure ? (
                  <FailureRow colSpan={6} query={meetingsQuery} kind={meetingsFailure} subject="pre-job meetings" />
                ) : pendingOf(meetingsQuery, meetingsFailure) ? (
                  <LoadingRow colSpan={6} />
                ) : filteredMeetings.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-fieldiq-muted">No meetings found</td></tr>
                ) : filteredMeetings.map((m: MeetingRow) => (
                  <tr key={m.id} className="border-b border-fieldiq-border/50 hover:bg-white/5 transition-colors">
                    {/* `m.date` was never a key on this payload — the real one is
                      * `meetingDate`, and the phantom first arm made the column
                      * look like it had a fallback it never used. */}
                    <td className="px-4 py-3 text-foreground">{fmtDay(m.meetingDate)}</td>
                    {/* Was defaulted to 'pre_job'. `meeting_type` is an
                      * unconstrained varchar and POST /safety/meetings defaults
                      * it to 'general', so a blank one is not a pre-job meeting
                      * — it is a meeting nobody typed. Say so. */}
                    <td className="px-4 py-3 text-fieldiq-text capitalize">{m.meetingType ? m.meetingType.replace(/_/g, ' ') : '—'}</td>
                    <td className="px-4 py-3 text-fieldiq-accent font-medium">{m.jobNumber || '—'}</td>
                    <td className="px-4 py-3 text-fieldiq-text">{m.location || '—'}</td>
                    {/* Was defaulted to 'completed'. The column defaults to
                      * 'scheduled', so an absent status rendered as the one
                      * value that says the meeting definitely happened. */}
                    <td className="px-4 py-3">{m.status ? <StatusBadge status={m.status} /> : <span className="text-fieldiq-muted">—</span>}</td>
                    <td className="px-4 py-3 text-right sticky right-0 bg-fieldiq-card">
                      <button
                        onClick={() => handleDownloadPdf('meeting', m.id)}
                        disabled={pdfLoading === m.id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-fieldiq-accent hover:bg-fieldiq-accent/10 rounded transition-colors"
                      >
                        {pdfLoading === m.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}

          {activeTab === 'jsas' && (
            <div className="w-full overflow-x-auto">
            <table className="min-w-[640px] w-full text-sm">
              <thead>
                <tr className="border-b border-fieldiq-border text-fieldiq-muted text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-3 font-medium">Date</th>
                  <th className="text-left px-4 py-3 font-medium">Title</th>
                  <th className="text-left px-4 py-3 font-medium">Job</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-right px-4 py-3 font-medium sticky right-0 bg-fieldiq-card">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jsaFailure ? (
                  <FailureRow colSpan={5} query={jsaQuery} kind={jsaFailure} subject="JSAs" />
                ) : pendingOf(jsaQuery, jsaFailure) ? (
                  <LoadingRow colSpan={5} />
                ) : filteredJsas.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-fieldiq-muted">No JSAs found</td></tr>
                ) : filteredJsas.map((j) => (
                  <tr key={j.id} onClick={() => setDetailJsaId(j.id)} className="border-b border-fieldiq-border/50 hover:bg-white/5 transition-colors cursor-pointer">
                    <td className="px-4 py-3 text-foreground">{fmtDate(j.createdAt, timeZone)}</td>
                    <td className="px-4 py-3">
                      <span className="text-foreground font-medium">{j.title || '—'}</span>
                      {j.status === 'rejected' && (
                        <p className="text-xs text-fieldiq-text mt-1 italic">
                          Rejected: {rejectionReasonText(j.rejectionReason)}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-fieldiq-accent font-medium">{j.jobNumber || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge status={j.status ?? ''} /></td>
                    <td className="px-4 py-3 text-right sticky right-0 bg-fieldiq-card">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownloadPdf('jsa', j.id); }}
                          disabled={pdfLoading === j.id}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-fieldiq-accent hover:bg-fieldiq-accent/10 rounded transition-colors"
                        >
                          {pdfLoading === j.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                          PDF
                        </button>
                        {isJsaEditable(j.status) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(jsaEditPath(j.id, '/safety/records')); }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-fieldiq-text hover:bg-fieldiq-dark rounded transition-colors"
                          >
                            <Pencil size={12} />
                            Edit
                          </button>
                        )}
                        {isJsaSubmittable(j.status) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setSubmitModalJsa(j); }}
                            className="inline-flex items-center gap-1 rounded-fi-pill border border-black/20 px-2.5 py-1 text-xs font-bold text-fi-signal-ink transition-opacity hover:opacity-90 bg-fi-signal-amber"
                          >
                            <CheckCircle size={12} />
                            {j.status === 'rejected' ? 'Resubmit' : 'Submit'}
                          </button>
                        )}
                        {j.status === 'submitted' && canApprove && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); setApproveModalJsa(j); }}
                              disabled={approvingId === j.id}
                              className="inline-flex items-center gap-1 rounded-fi-pill border border-black/20 px-2.5 py-1 text-xs font-bold text-fi-signal-ink transition-opacity hover:opacity-90 bg-fi-signal-green"
                            >
                              {approvingId === j.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                              Approve
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setRejectModalId(j.id); setRejectReason(''); }}
                              className="inline-flex items-center gap-1 rounded-fi-pill border border-black/20 px-2.5 py-1 text-xs font-bold text-fi-signal-ink transition-opacity hover:opacity-90 bg-fi-signal-red"
                            >
                              <XCircle size={12} />
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}

          {activeTab === 'incidents' && (
            <div className="w-full overflow-x-auto">
            <table className="min-w-[720px] w-full text-sm">
              <thead>
                <tr className="border-b border-fieldiq-border text-fieldiq-muted text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-3 font-medium">Date</th>
                  <th className="text-left px-4 py-3 font-medium">Incident #</th>
                  <th className="text-left px-4 py-3 font-medium">Type</th>
                  <th className="text-left px-4 py-3 font-medium">Location</th>
                  <th className="text-left px-4 py-3 font-medium">Severity</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-right px-4 py-3 font-medium sticky right-0 bg-fieldiq-card">Actions</th>
                </tr>
              </thead>
              <tbody>
                {incidentsFailure ? (
                  <FailureRow colSpan={7} query={incidentsQuery} kind={incidentsFailure} subject="incidents" />
                ) : pendingOf(incidentsQuery, incidentsFailure) ? (
                  <LoadingRow colSpan={7} />
                ) : filteredIncidents.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-fieldiq-muted">No incidents found</td></tr>
                ) : filteredIncidents.map((inc: any) => (
                  <tr key={inc.id} onClick={() => setDetailIncidentId(inc.id)} className="border-b border-fieldiq-border/50 hover:bg-white/5 transition-colors cursor-pointer">
                    <td className="px-4 py-3 text-foreground">{fmtDay(inc.incidentDate || inc.incident_date)}</td>
                    <td className="px-4 py-3 text-fieldiq-accent font-medium">{inc.incidentNumber || inc.incident_number || '—'}</td>
                    <td className="px-4 py-3 text-fieldiq-text capitalize">{(inc.incidentType || inc.incident_type || '').replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3 text-fieldiq-text">{inc.location || '—'}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={inc.severityLevel || inc.severity_level || inc.severity || 'low'} />
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={inc.status} /></td>
                    <td className="px-4 py-3 text-right sticky right-0 bg-fieldiq-card">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownloadPdf('incident', inc.id); }}
                        disabled={pdfLoading === inc.id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-fieldiq-accent hover:bg-fieldiq-accent/10 rounded transition-colors"
                      >
                        {pdfLoading === inc.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}

          {activeTab === 'inspections' && rentalTab && (
            <RentalInspectionsTable
              rows={filteredRentals}
              failure={rentalFailure}
              query={rentalQuery}
              titled={tripTab}
              onOpen={() => navigate('/rentals/inspections')}
            />
          )}

          {activeTab === 'inspections' && tripTab && (
            <TripInspectionsTable
              rows={filteredTrips}
              failure={tripFailure}
              query={tripQuery}
              timeZone={timeZone}
              titled={rentalTab}
              tripType={tripType}
              onTripType={setTripType}
              onOpen={(id) => navigate(`/trucking/trip-inspections/${id}`)}
            />
          )}

          {activeTab === 'inspections' && casingTenant && (
            inspectionsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" />
                <span className="ml-2 text-fieldiq-muted text-sm">Loading inspections...</span>
              </div>
            ) : (
            <div className="w-full overflow-x-auto">
            <table className="min-w-[700px] w-full text-sm">
              <thead>
                <tr className="border-b border-fieldiq-border text-fieldiq-muted text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-3 font-medium">Date</th>
                  <th className="text-left px-4 py-3 font-medium">Form Type</th>
                  <th className="text-left px-4 py-3 font-medium">Equipment</th>
                  <th className="text-left px-4 py-3 font-medium">Inspector</th>
                  <th className="text-left px-4 py-3 font-medium">Result</th>
                  <th className="text-left px-4 py-3 font-medium sticky right-0 bg-fieldiq-card">Actions</th>
                </tr>
              </thead>
              <tbody>
                {inspectionsFailure ? (
                  <FailureRow colSpan={6} query={inspectionsQuery} kind={inspectionsFailure} subject="submitted inspections" />
                ) : inspectionsLoading ? (
                  <LoadingRow colSpan={6} />
                ) : filteredInspections.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-fieldiq-muted">No submitted inspections found</td></tr>
                ) : filteredInspections.map((insp: any) => (
                  <tr
                    key={insp.id}
                    className="border-b border-fieldiq-border/50 hover:bg-white/5 transition-colors"
                  >
                    <td className="px-4 py-3 text-foreground">{insp.inspection_date ? fmtDay(insp.inspection_date) : fmtDate(insp.created_at, timeZone)}</td>
                    <td className="px-4 py-3 text-foreground font-medium">
                      {inspectionFormTypeLabel(insp)}
                    </td>
                    <td className="px-4 py-3 text-fieldiq-text">
                      {insp.tong_make ? `${insp.tong_make}${insp.tong_model ? ` ${insp.tong_model}` : ''}` : insp.serial_number || '—'}
                    </td>
                    <td className="px-4 py-3 text-fieldiq-text">{insp.inspector_name || insp.operator_name || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge status={insp.overall_result || insp.status || '—'} /></td>
                    <td className="px-4 py-3 sticky right-0 bg-fieldiq-card">
                      <button
                        onClick={() => navigate(`/safety/inspections/${insp.id}`)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-fieldiq-accent hover:bg-fieldiq-accent/10 rounded transition-colors"
                      >
                        <Eye size={12} /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            )
          )}

          {activeTab === 'near_misses' && (
            <div className="w-full overflow-x-auto">
            <table className="min-w-[640px] w-full text-sm">
              <thead>
                <tr className="border-b border-fieldiq-border text-fieldiq-muted text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-3 font-medium">Date</th>
                  <th className="text-left px-4 py-3 font-medium">Title</th>
                  <th className="text-left px-4 py-3 font-medium">Location</th>
                  <th className="text-left px-4 py-3 font-medium">Severity</th>
                  <th className="text-left px-4 py-3 font-medium">Reported By</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {nearMissFailure ? (
                  <FailureRow colSpan={6} query={nearMissQuery} kind={nearMissFailure} subject="near miss reports" />
                ) : pendingOf(nearMissQuery, nearMissFailure) ? (
                  <LoadingRow colSpan={6} />
                ) : filteredNearMisses.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-fieldiq-muted">No near miss reports found</td></tr>
                ) : filteredNearMisses.map((nm: any) => (
                  <tr
                    key={nm.id}
                    onClick={() => setDetailIncidentId(nm.id)}
                    className="border-b border-fieldiq-border/50 hover:bg-white/5 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 text-foreground">{nm.created_at || nm.createdAt ? fmtDate(nm.created_at || nm.createdAt, timeZone) : fmtDay(nm.date)}</td>
                    <td className="px-4 py-3 text-foreground font-medium">{nm.title}</td>
                    <td className="px-4 py-3 text-fieldiq-text">{nm.location || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge status={nm.severity || 'low'} /></td>
                    <td className="px-4 py-3 text-fieldiq-text">
                      {[nm.first_name, nm.last_name].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={nm.status || 'open'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}

          {activeTab === 'custom_forms' && (
            <>
              {cfLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={24} className="animate-spin text-fieldiq-accent" />
                </div>
              ) : cfFailure ? (
                <LoadFailureNotice
                  error={queryFailureError(cfSubmissionsQuery)}
                  kind={cfFailure}
                  subject="form submissions"
                />
              ) : filteredCustomForms.length === 0 ? (
                <div className="text-center py-12">
                  <FileText size={48} className="mx-auto text-fieldiq-muted mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No submissions found</h3>
                  <p className="text-fieldiq-text">Adjust your filters or wait for form submissions.</p>
                </div>
              ) : (
                <div className="w-full overflow-x-auto">
                <table className="min-w-[640px] w-full text-sm">
                  <thead>
                    <tr className="border-b border-fieldiq-border text-fieldiq-muted text-xs uppercase tracking-wider">
                      <th className="text-left px-4 py-3 font-medium">Date</th>
                      <th className="text-left px-4 py-3 font-medium">Form Name</th>
                      <th className="text-left px-4 py-3 font-medium">Category</th>
                      <th className="text-left px-4 py-3 font-medium">Submitted By</th>
                      <th className="text-left px-4 py-3 font-medium">Status</th>
                      <th className="text-right px-4 py-3 font-medium sticky right-0 bg-fieldiq-card">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomForms.map((cf: any) => (
                      <tr
                        key={cf.id}
                        onClick={() => openCustomForm(cf)}
                        className="border-b border-fieldiq-border/50 hover:bg-white/5 transition-colors cursor-pointer"
                      >
                        {/* A draft has no submitted_at; its date is when it was last saved. */}
                        <td className="px-4 py-3 text-foreground">{fmtDate(cf.submitted_at || cf.updated_at || cf.created_at, timeZone)}</td>
                        <td className="px-4 py-3 text-foreground font-medium">{cf.template_name || '—'}</td>
                        <td className="px-4 py-3 text-fieldiq-text capitalize">{(cf.template_category || '—').replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3 text-fieldiq-text">{cf.submitter_name || '—'}</td>
                        <td className="px-4 py-3"><StatusBadge status={cf.status || 'submitted'} /></td>
                        <td className="px-4 py-3 text-right sticky right-0 bg-fieldiq-card">
                          <button
                            onClick={(e) => { e.stopPropagation(); openCustomForm(cf); }}
                            aria-label={`${isOwnDraft(cf) ? 'Resume' : 'View'} ${cf.template_name || 'submission'}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-fieldiq-accent hover:bg-fieldiq-accent/10 rounded transition-colors"
                          >
                            {isOwnDraft(cf) ? <FilePen size={12} /> : <Eye size={12} />}
                            {isOwnDraft(cf) ? 'Resume' : 'View'}
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              setPdfLoading(cf.id);
                              await downloadSubmissionPdf(cf.id, submissionPdfFilename(cf.template_name, cf.id));
                              setPdfLoading(null);
                            }}
                            disabled={pdfLoading === cf.id}
                            aria-label={`PDF of ${cf.template_name || 'submission'}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-fieldiq-accent hover:bg-fieldiq-accent/10 rounded transition-colors disabled:opacity-50"
                          >
                            {pdfLoading === cf.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                            PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Custom Forms pagination */}
        {activeTab === 'custom_forms' && cfTotalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-4">
            <button
              onClick={() => setCfPage(p => Math.max(1, p - 1))}
              disabled={cfPage <= 1}
              className="px-3 py-1.5 bg-fieldiq-card border border-fieldiq-border rounded-lg text-sm text-fieldiq-text hover:text-foreground disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-fieldiq-text">
              Page {cfPage} of {cfTotalPages}
            </span>
            <button
              onClick={() => setCfPage(p => Math.min(cfTotalPages, p + 1))}
              disabled={cfPage >= cfTotalPages}
              className="px-3 py-1.5 bg-fieldiq-card border border-fieldiq-border rounded-lg text-sm text-fieldiq-text hover:text-foreground disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}

      </div>

      {/* ── Approve JSA Modal ── */}
      {approveModalJsa && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 w-full max-w-md mx-4 space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Approve JSA?</h3>
            <p className="text-sm text-fieldiq-text">
              Approve <span className="text-foreground font-medium">{approveModalJsa.title || 'this JSA'}</span>
              {approveModalJsa.createdAt &&
                ` from ${fmtDate(approveModalJsa.createdAt, timeZone)}`
              }? This action cannot be undone from the UI.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setApproveModalJsa(null)}
                disabled={approvingId === approveModalJsa.id}
                className="px-4 py-2 text-sm text-fieldiq-text hover:text-foreground rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleApprove(approveModalJsa.id)}
                disabled={approvingId === approveModalJsa.id}
                className="px-4 py-2 text-sm font-bold text-fi-signal-ink border border-black/20 rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50 bg-fi-signal-green"
              >
                {approvingId === approveModalJsa.id ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject JSA Modal ── */}
      {rejectModalId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 w-full max-w-md mx-4 space-y-4">
            <h3 className="text-lg font-semibold text-foreground">Reject JSA</h3>
            <div>
              <label className="text-sm text-fieldiq-text block mb-1">Reason for rejection *</label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejecting this JSA..."
                rows={3}
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-fieldiq-muted focus:border-fieldiq-accent outline-none resize-none"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setRejectModalId(null); setRejectReason(''); }}
                className="px-4 py-2 text-sm text-fieldiq-text hover:text-foreground rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={!rejectReason.trim() || rejectSubmitting}
                className="px-4 py-2 text-sm font-bold text-fi-signal-ink border border-black/20 rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50 bg-fi-signal-red"
              >
                {rejectSubmitting ? 'Rejecting...' : 'Reject JSA'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Incident / Near Miss Detail Modal ── */}
      <IncidentDetailModal
        incidentId={detailIncidentId}
        isOpen={!!detailIncidentId}
        onClose={() => setDetailIncidentId(null)}
      />

      {/* ── JSA Detail Modal ── */}
      <JSADetailModal
        jsaId={detailJsaId}
        isOpen={!!detailJsaId}
        onClose={() => setDetailJsaId(null)}
      />

      {/* ── Submit JSA Modal ── */}
      {submitModalJsa && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 w-full max-w-md mx-4 space-y-4">
            <h3 className="text-lg font-semibold text-foreground">
              {submitModalJsa.status === 'rejected' ? 'Resubmit JSA for Approval?' : 'Submit JSA for Approval?'}
            </h3>
            {/* The reason goes in front of whoever resubmits, before they send it
                back — it is the thing the approver will be checking for. */}
            {submitModalJsa.status === 'rejected' && (
              <div
                role="note"
                aria-label="Rejection reason"
                className="rounded-lg border border-fieldiq-border border-l-4 border-l-fi-signal-red bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text"
              >
                <span className="font-semibold text-foreground">Rejected: </span>
                {rejectionReasonText(submitModalJsa.rejectionReason)}
              </div>
            )}
            <p className="text-sm text-fieldiq-text">
              Submit <span className="text-foreground font-medium">{submitModalJsa.title || 'this JSA'}</span>
              {submitModalJsa.createdAt &&
                ` from ${fmtDate(submitModalJsa.createdAt, timeZone)}`
              } for supervisor approval?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setSubmitModalJsa(null)}
                disabled={submittingId === submitModalJsa.id}
                className="px-4 py-2 text-sm text-fieldiq-text hover:text-foreground rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSubmitJsa(submitModalJsa.id)}
                disabled={submittingId === submitModalJsa.id}
                className="px-4 py-2 text-sm font-bold text-fi-signal-ink border border-black/20 rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50 bg-fi-signal-amber"
              >
                {submittingId === submitModalJsa.id
                  ? 'Submitting...'
                  : submitModalJsa.status === 'rejected' ? 'Resubmit' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The Inspections tab for a trucking tenant with no casing: its trip
 * inspections (api#855), opening the trucking detail page. Three states, as
 * every list here: rows, none, or what failed.
 */
function TripInspectionsTable({ rows, failure, query, timeZone, titled, tripType, onTripType, onOpen }: {
  rows: TripInspectionListRow[];
  failure: LoadFailureKind | null;
  query: Parameters<typeof queryFailureError>[0] & { data?: unknown };
  timeZone: string | null;
  /** Shown above when the rental list sits beside it on the same tab. */
  titled: boolean;
  /** Pre-trip, post-trip or both (api 0437). Applied by the parent, so the tab's count agrees. */
  tripType: 'all' | TripInspectionType;
  onTripType: (t: 'all' | TripInspectionType) => void;
  onOpen: (id: string) => void;
}) {
  return (
    <div className="w-full overflow-x-auto" data-testid="records-trip-inspections">
      {titled && <h3 className="px-4 pt-4 text-sm font-semibold text-fieldiq-text">Trip inspections</h3>}
      <div className="flex items-center gap-2 px-4 pt-3">
        <label htmlFor="records-trip-type" className="text-xs font-medium uppercase tracking-wider text-fieldiq-muted">Type</label>
        <select
          id="records-trip-type"
          value={tripType}
          onChange={(e) => onTripType(e.target.value as 'all' | TripInspectionType)}
          className="rounded-lg border border-fieldiq-border bg-fieldiq-card px-2 py-1 text-sm text-fieldiq-text"
        >
          <option value="all">All</option>
          {TRIP_TYPES.map((t) => <option key={t} value={t}>{TRIP_TYPE_LABEL[t]}</option>)}
        </select>
      </div>
      <table className="min-w-[700px] w-full text-sm">
        <thead>
          <tr className="border-b border-fieldiq-border text-fieldiq-muted text-xs uppercase tracking-wider">
            <th className="text-left px-4 py-3 font-medium">Inspected</th>
            <th className="text-left px-4 py-3 font-medium">Type</th>
            <th className="text-left px-4 py-3 font-medium">Units</th>
            <th className="text-left px-4 py-3 font-medium">Driver</th>
            <th className="text-left px-4 py-3 font-medium">Result</th>
            <th className="text-left px-4 py-3 font-medium sticky right-0 bg-fieldiq-card">Actions</th>
          </tr>
        </thead>
        <tbody>
          {failure ? (
            <FailureRow colSpan={6} query={query as any} kind={failure} subject="trip inspections" />
          ) : query.data === undefined ? (
            <LoadingRow colSpan={6} />
          ) : rows.length === 0 ? (
            <tr><td colSpan={6} className="px-4 py-8 text-center text-fieldiq-muted">No trip inspections found</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id} className="border-b border-fieldiq-border/50 hover:bg-white/5 transition-colors">
              <td className="px-4 py-3 text-foreground">{formatInstant(r.inspected_at, timeZone) ?? '—'}</td>
              <td className="px-4 py-3 text-fieldiq-text">{tripTypeLabel(r.inspection_type)}</td>
              <td className="px-4 py-3 text-foreground font-medium">{r.units.map(u => u.unit_number).join(', ') || '—'}</td>
              <td className="px-4 py-3 text-fieldiq-text">
                {personName(r.driver_first_name, r.driver_last_name)}
                {r.entered_by_office && <span className="ml-2 text-xs text-fieldiq-muted">Entered by office</span>}
              </td>
              <td className="px-4 py-3"><OutcomeBadge outcome={reportOutcome(r)} /></td>
              <td className="px-4 py-3 sticky right-0 bg-fieldiq-card">
                <button
                  onClick={() => onOpen(r.id)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-fieldiq-accent hover:bg-fieldiq-accent/10 rounded transition-colors"
                >
                  <Eye size={12} /> View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type RentalInspectionApiRow = Parameters<typeof normalizeInspection>[0];
type RentalInspectionRow = ReturnType<typeof normalizeInspection>;

const RENTAL_GRADE: Record<'pass' | 'minor' | 'fail', { label: string; fill: string }> = {
  pass: { label: 'Pass', fill: 'bg-fi-signal-green' },
  minor: { label: 'Minor', fill: 'bg-fi-signal-amber' },
  fail: { label: 'Fail', fill: 'bg-fi-signal-red' },
};

/**
 * The Inspections tab for a rentals tenant with no casing: its rental
 * inspections (api#861), opening the rental Inspections register. An overall
 * this screen cannot read is a slate dash, never a Pass.
 */
function RentalInspectionsTable({ rows, failure, query, titled, onOpen }: {
  rows: RentalInspectionRow[];
  failure: LoadFailureKind | null;
  query: Parameters<typeof queryFailureError>[0] & { data?: unknown };
  /** Shown above when the trip list follows it on the same tab. */
  titled: boolean;
  onOpen: () => void;
}) {
  return (
    <div className="w-full overflow-x-auto" data-testid="records-rental-inspections">
      {titled && <h3 className="px-4 pt-4 text-sm font-semibold text-fieldiq-text">Rental equipment</h3>}
      <table className="min-w-[700px] w-full text-sm">
        <thead>
          <tr className="border-b border-fieldiq-border text-fieldiq-muted text-xs uppercase tracking-wider">
            <th className="text-left px-4 py-3 font-medium">Inspected</th>
            <th className="text-left px-4 py-3 font-medium">Asset</th>
            <th className="text-left px-4 py-3 font-medium">Type</th>
            <th className="text-left px-4 py-3 font-medium">Result</th>
            <th className="text-left px-4 py-3 font-medium sticky right-0 bg-fieldiq-card">Actions</th>
          </tr>
        </thead>
        <tbody>
          {failure ? (
            <FailureRow colSpan={5} query={query as any} kind={failure} subject="rental inspections" />
          ) : query.data === undefined ? (
            <LoadingRow colSpan={5} />
          ) : rows.length === 0 ? (
            <tr><td colSpan={5} className="px-4 py-8 text-center text-fieldiq-muted">No rental inspections found</td></tr>
          ) : rows.map((r) => {
            const g = r.overall ? RENTAL_GRADE[r.overall] : { label: '\u2014', fill: 'bg-fi-signal-slate' };
            return (
              <tr key={r.id} className="border-b border-fieldiq-border/50 hover:bg-white/5 transition-colors">
                <td className="px-4 py-3 text-foreground">{r.when}</td>
                <td className="px-4 py-3 text-foreground font-medium">{r.asset_name} <span className="text-xs text-fieldiq-muted">{r.asset_tag}</span></td>
                <td className="px-4 py-3 text-fieldiq-text">{r.type === 'pre_delivery' ? 'Pre-Delivery' : r.type === 'post_return' ? 'Post-Return' : '\u2014'}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${g.fill}`}>{g.label}</span>
                </td>
                <td className="px-4 py-3 sticky right-0 bg-fieldiq-card">
                  <button onClick={onOpen} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-fieldiq-accent hover:bg-fieldiq-accent/10 rounded transition-colors">
                    <Eye size={12} /> View
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
